import { 
  Plus, 
  Edit, 
  Trash2, 
  Key, 
  LogOut, 
  Eye, 
  FileText, 
  User,
  Shield,
  Database,
  Activity
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

// Action types and their configurations (basado en actionDescription)
export const ACTION_CONFIG: Record<string, {
  icon: LucideIcon
  color: string
  bgColor: string
  label: string
}> = {
  // Auth actions
  'USER_LOGGED_IN': {
    icon: Key,
    color: 'text-green-600',
    bgColor: 'bg-green-50',
    label: 'User Login'
  },
  'USER_LOGGED_OUT': {
    icon: LogOut,
    color: 'text-gray-600',
    bgColor: 'bg-gray-50',
    label: 'User Logout'
  },
  // CRUD actions
  'CREATE': {
    icon: Plus,
    color: 'text-green-600',
    bgColor: 'bg-green-50',
    label: 'Created'
  },
  'UPDATE': {
    icon: Edit,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    label: 'Updated'
  },
  'DELETE': {
    icon: Trash2,
    color: 'text-red-600',
    bgColor: 'bg-red-50',
    label: 'Deleted'
  },
  'VIEW': {
    icon: Eye,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    label: 'Viewed'
  },
  'EXPORT': {
    icon: FileText,
    color: 'text-indigo-600',
    bgColor: 'bg-indigo-50',
    label: 'Exported'
  }
}

// Entity type configurations
export const ENTITY_CONFIG: Record<string, {
  icon: LucideIcon
  label: string
}> = {
  users: {
    icon: User,
    label: 'User'
  },
  roles: {
    icon: Shield,
    label: 'Role'
  },
  jobRequests: {
    icon: FileText,
    label: 'Job Request'
  },
  catalogos: {
    icon: Database,
    label: 'Catalog'
  },
  auth: {
    icon: Key,
    label: 'Authentication'
  }
}

// Get action configuration with fallback
export const getActionConfig = (actionDescription: string | undefined | null): { icon: LucideIcon; color: string; bgColor: string; label: string } => {
  if (!actionDescription || typeof actionDescription !== 'string') {
    return {
      icon: Activity,
      color: 'text-gray-600',
      bgColor: 'bg-gray-50',
      label: 'Unknown Action'
    }
  }
  
  // Buscar configuraciones que coincidan con la descripción
  const upperAction = actionDescription.toUpperCase()
  
  // Primero busca coincidencias exactas
  if (ACTION_CONFIG[upperAction]) {
    return ACTION_CONFIG[upperAction]
  }

  // Luego busca coincidencias parciales
  if (upperAction.includes('LOGIN') || upperAction.includes('LOGGED_IN')) {
    return ACTION_CONFIG['USER_LOGGED_IN']
  }
  if (upperAction.includes('LOGOUT') || upperAction.includes('LOGGED_OUT')) {
    return ACTION_CONFIG['USER_LOGGED_OUT']
  }
  if (upperAction.includes('CREATE') || upperAction.includes('POST')) {
    return ACTION_CONFIG['CREATE']
  }
  if (upperAction.includes('UPDATE') || upperAction.includes('PUT') || upperAction.includes('PATCH')) {
    return ACTION_CONFIG['UPDATE']
  }
  if (upperAction.includes('DELETE')) {
    return ACTION_CONFIG['DELETE']
  }
  if (upperAction.includes('GET') || upperAction.includes('VIEW') || upperAction.includes('READ')) {
    return ACTION_CONFIG['VIEW']
  }
  if (upperAction.includes('EXPORT')) {
    return ACTION_CONFIG['EXPORT']
  }

  // Fallback usando la descripción original
  return {
    icon: Activity,
    color: 'text-gray-600',
    bgColor: 'bg-gray-50',
    label: actionDescription
  }
}

// Get entity configuration with fallback (módulo)
export const getEntityConfig = (module: string | undefined | null): { icon: LucideIcon; label: string } => {
  if (!module || typeof module !== 'string') {
    return {
      icon: Database,
      label: 'Unknown Module'
    }
  }
  
  // Mapeo de módulos a configuraciones
  const moduleConfig: Record<string, { icon: LucideIcon; label: string }> = {
    users: {
      icon: User,
      label: 'Users'
    },
    auth: {
      icon: Key,
      label: 'Authentication'
    },
    'job-requests': {
      icon: FileText,
      label: 'Job Requests'
    },
    catalogos: {
      icon: Database,
      label: 'Catalogs'
    }
  }
  
  return moduleConfig[module.toLowerCase()] || {
    icon: Database,
    label: module
  }
}

// Format relative time
export const formatRelativeTime = (dateString: string | undefined | null): string => {
  if (!dateString || typeof dateString !== 'string') {
    return 'Unknown time'
  }
  
  const date = new Date(dateString)
  
  // Check if date is valid
  if (isNaN(date.getTime())) {
    return 'Invalid date'
  }
  
  const now = new Date()
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000)

  if (diffInSeconds < 60) {
    return 'hace unos momentos'
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60)
  if (diffInMinutes < 60) {
    return `hace ${diffInMinutes} minuto${diffInMinutes > 1 ? 's' : ''}`
  }

  const diffInHours = Math.floor(diffInMinutes / 60)
  if (diffInHours < 24) {
    return `hace ${diffInHours} hora${diffInHours > 1 ? 's' : ''}`
  }

  const diffInDays = Math.floor(diffInHours / 24)
  if (diffInDays < 7) {
    if (diffInDays === 1) {
      return 'ayer'
    }
    return `hace ${diffInDays} días`
  }

  // For dates older than 7 days, use standard format
  return date.toLocaleDateString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

// Format IP address (show only last digits for privacy)
export const formatIpAddress = (ipAddress: string | undefined | null): string => {
  if (!ipAddress || typeof ipAddress !== 'string') return 'N/A'
  
  const parts = ipAddress.split('.')
  if (parts.length === 4) {
    return `...${parts[2]}.${parts[3]}`
  }
  
  // For IPv6 or other formats
  if (ipAddress.length > 12) {
    return `...${ipAddress.slice(-8)}`
  }
  
  return ipAddress
}

// Generate description for audit action
export const generateAuditDescription = (log: any): string => {
  if (!log) {
    return 'Unknown action'
  }
  
  // Si ya tenemos actionDescription, usarla directamente
  if (log.actionDescription && typeof log.actionDescription === 'string') {
    return log.actionDescription
  }
  
  const actionConfig = getActionConfig(log.actionDescription)
  const entityConfig = getEntityConfig(log.module)
  
  // Fallback usando la configuración
  return `${actionConfig.label} in ${entityConfig.label}`
}

// Extract meaningful information from request/response data
export const formatRequestDetails = (requestData: any, responseData: any): string => {
  if (!requestData && !responseData) return 'No details available'
  
  const details: string[] = []
  
  // Info from request
  if (requestData && typeof requestData === 'object') {
    if (requestData.body) {
      const bodyKeys = Object.keys(requestData.body).filter(key => 
        !key.includes('password') && !key.includes('token')
      )
      if (bodyKeys.length > 0) {
        details.push(`Modified fields: ${bodyKeys.slice(0, 3).join(', ')}`)
      }
    }
    if (requestData.params) {
      const paramKeys = Object.keys(requestData.params)
      if (paramKeys.length > 0) {
        details.push(`Parameters: ${paramKeys.slice(0, 2).join(', ')}`)
      }
    }
  }
  
  // Info from response
  if (responseData && typeof responseData === 'object') {
    if (responseData.id) {
      details.push(`ID: ${responseData.id}`)
    }
    if (responseData.message) {
      details.push(`Result: ${responseData.message}`)
    }
  }
  
  if (details.length === 0) {
    return 'No specific details recorded'
  }
  
  return details.slice(0, 2).join(' | ')
}

// Parse and format JSON string data
export const parseJsonData = (data: string | any): any => {
  if (!data) return null
  
  if (typeof data === 'string') {
    try {
      return JSON.parse(data)
    } catch {
      return data
    }
  }
  
  return data
}

// Format JSON data for display
export const formatJsonForDisplay = (data: any, maxItems: number = 5): string => {
  if (!data) return 'N/A'
  
  const parsedData = parseJsonData(data)
  
  if (typeof parsedData === 'object' && parsedData !== null) {
    const entries = Object.entries(parsedData)
    const displayEntries = entries.slice(0, maxItems)
    
    const formatted = displayEntries.map(([key, value]) => {
      if (key.toLowerCase().includes('password') || key.toLowerCase().includes('token')) {
        return `${key}: ***`
      }
      
      let displayValue: string
      if (typeof value === 'string' && value.length > 50) {
        displayValue = value.substring(0, 50) + '...'
      } else if (typeof value === 'object') {
        const jsonString = JSON.stringify(value)
        if (jsonString.length > 50) {
          displayValue = jsonString.substring(0, 50) + '...'
        } else {
          displayValue = jsonString
        }
      } else {
        displayValue = String(value)
        if (displayValue.length > 50) {
          displayValue = displayValue.substring(0, 50) + '...'
        }
      }
      
      return `${key}: ${displayValue}`
    }).join('\n')
    
    if (entries.length > maxItems) {
      return formatted + `\n... and ${entries.length - maxItems} more fields`
    }
    
    return formatted
  }
  
  return String(parsedData)
}